// The host per branch (spec/dashboard.md section 10.2) and how the shell reaches it: the provider lives inside the
// branch's ContentProvider (it needs the catalog and the workspace); the shell above it finds the host through a small
// registry keyed by the workspace, and re-renders on the host's state and log changes.
import { signedAssetUrls } from '../api/assetUrls';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { ops } from '../api/operations';
import type { Document } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useContent } from '../content/ContentContext';
import { heirsOf } from '../content/widgets/context';
import { useGame } from '../shell/GameContext';
import { useWorkspace } from '../workspace/react';
import type { EditorAdapter } from './editorApi';
import { ExtensionHost, type SandboxBackend } from './host';
import type { ScriptSource } from './protocol';

const registry = new Map<string, ExtensionHost>();
const listeners = new Set<() => void>();
let version = 0;
const notify = () => {
  version++;
  for (const l of listeners) l();
};

let backendFactory: (() => SandboxBackend) | null = null;

/** The sandbox backend: the Worker in the browser; tests inject the vm backend. */
export function configureExtensionBackend(factory: (() => SandboxBackend) | null): void {
  backendFactory = factory;
}

async function defaultBackend(): Promise<SandboxBackend> {
  if (backendFactory) return backendFactory();
  if (typeof Worker === 'function') {
    const { workerBackend } = await import('./workerBackend');
    return workerBackend;
  }
  return { start: () => { throw new Error('No sandbox is available in this environment.'); } };
}

/** The host of a workspace (branch) as the shell sees it; re-renders on its events. */
export function useExtensionHost(key: string | null): { host: ExtensionHost | null; version: number } {
  const [v, setV] = useState(version);
  useEffect(() => {
    const l = () => setV(version);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  return { host: key ? (registry.get(key) ?? null) : null, version: v };
}

export function hostOf(key: string): ExtensionHost | undefined {
  return registry.get(key);
}

const Ctx = createContext<ExtensionHost | null>(null);

export function useExtensions(): ExtensionHost | null {
  return useContext(Ctx);
}

/** Creates the branch's host, loads the enabled scripts, registers it for the shell, fires postSave after a save. */
export function ExtensionsProvider({ children }: { children: ReactNode }) {
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const catalog = useContent();
  const workspace = useWorkspace();
  const [host, setHost] = useState<ExtensionHost | null>(null);

  const adapter = useMemo<EditorAdapter>(
    () => ({
      catalog,
      workspace,
      listDocuments: (template, includeHeirs) =>
        client.all<Document>((cursor) => client.data(ops.listDocuments, { path: { org, game: game.slug, branch }, query: { template, includeHeirs: includeHeirs ? 'true' : undefined, limit: 200, cursor: cursor ?? undefined } })),
      assetUrls: (ids) => signedAssetUrls({ client, org, game: game.slug, branch }, ids),
      getDocument: async (id) => {
        try {
          return (await client.data(ops.getDocument, { path: { org, game: game.slug, branch, document: /^[0-9a-f-]{36}$/i.test(id) || id.startsWith('$') ? id : `unny:${id}` } })) as Document;
        } catch {
          return undefined;
        }
      },
    }),
    [catalog, workspace, client, org, game.slug, branch],
  );

  useEffect(() => {
    if (!branch || catalog.loading) return;
    let cancelled = false;
    const loadScripts = async (): Promise<ScriptSource[]> => {
      const list = await client.data(ops.listExtensions, { path: { org, game: game.slug, branch } });
      const enabled = (list.items ?? []).filter((e) => e.enabled);
      const sources = await Promise.all(enabled.map((e) => client.data(ops.getExtension, { path: { org, game: game.slug, branch, name: e.name } })));
      return sources.map((s) => ({ name: s.name, source: s.source ?? '' }));
    };
    let created: ExtensionHost | null = null;
    void (async () => {
      const backend = await defaultBackend();
      if (cancelled) return;
      created = new ExtensionHost({ backend, adapter, loadScripts, heirsOf: (t) => heirsOf(catalog, t).map((h) => h.name), onState: notify, onLog: notify });
      registry.set(workspace.branch, created);
      setHost(created);
      notify();
      await created.load();
    })();
    return () => {
      cancelled = true;
      if (created) {
        void created.dispose();
        if (registry.get(workspace.branch) === created) registry.delete(workspace.branch);
        notify();
      }
      setHost(null);
    };
    // the host is rebuilt per branch; the adapter follows the catalog and the workspace
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, workspace.branch, catalog.loading]);

  useEffect(() => {
    if (host) host.setAdapter(adapter);
  }, [host, adapter]);

  // postSave after a successful Save All (10.6): the last save's id mapping with the entities the hook saw
  useEffect(() => {
    if (!host) return;
    let last = workspace.lastSave;
    return workspace.subscribe(() => {
      const now = workspace.lastSave;
      if (now === last) return;
      last = now;
      if (!now || now.dryRun || now.saved === 0) return;
      const ctx = host.lastPreSaveCtx;
      if (!ctx) return;
      host.lastPreSaveCtx = null;
      void host.postSave({ idMapping: now.idMapping ?? {}, changedEntities: ctx.changedEntities });
    });
  }, [host, workspace]);

  return <Ctx.Provider value={host}>{children}</Ctx.Provider>;
}
